// Internal MATCHING tool. Staff say "Match"; the customer-facing surfaces say
// "Treffer" for the same thing — a deliberate register split, not drift.
import type { CriterionKey } from '../../utils/property-match'

import { COMMON } from './common'
import { TERMS } from './terms'

/** What the advisor writes beside a match, and the customer reads. */
const AGENT_NOTE = 'Anmerkung für den Kunden'

export const MATCHING = {
  // Robot: the background job that re-checks every mandate.
  robot: {
    title: 'Matching Robot',
    start: 'Suche starten',
    starting: 'Robot wird gestartet …',
    alreadyRunning: 'Läuft bereits',
    checkAll: 'Alle Suchaufträge prüfen',
    checking: (name: string) => `Prüfe ${name}`,
    mandates: 'Suchaufträge',
    mandatesActive: 'Suchaufträge aktiv',
    complete: 'abgeschlossen',
    matchesFound: 'Matches gefunden',
    connected: 'Verbunden',
    disconnected: 'Getrennt',
    startFailed: 'Robot konnte nicht gestartet werden.',
  },
  // Search mandate list and detail
  mandate: {
    advisor: 'Berater',
    refresh: 'Suchauftrag neu laden',
    refreshed: 'Suchauftrag wurde aktualisiert',
    refreshFailed: 'Aktualisierung fehlgeschlagen',
    plural: 'Suchaufträgen',
    none: 'Es gibt derzeit keine aktiven Suchaufträge.',
  },
  matches: {
    label: 'Matches',
    withCount: (n: number) => `Matches (${n})`,
    /** `status` is a match-status label — "Keine bestätigten Matches". */
    noneInCategory: (status: string) =>
      `Keine ${status.toLowerCase()}en Matches`,
    allLoaded: 'Alle Matches geladen',
    tryOtherFilter: 'Andere Auswahl versuchen.',
  },
  acknowledgeFeedback: 'Feedback bestätigen',
  createAccount: 'Kundenkonto anlegen',
  openCustomerView: 'Kundenansicht öffnen',
  clearHubSpotCache: 'HubSpot-Cache leeren',
  searchMandateOrCustomer: 'Suche nach Kunde oder Standort …',
  searchCriteria: 'Suchkriterien',
  notRented: 'Nicht vermietet',
  conditionAny:
    'Zustand: Gepflegt, Renovierungsbedürftig, Nach Vereinbarung, Keine Angaben',
  additionalNotes: 'Zusätzliche Informationen',
  districts: 'Stadtteile',
  lastChecked: 'Zuletzt geprüft',
  never: 'Nie',
  noResults: 'Keine Suchaufträge gefunden',
  loadError: 'Suchaufträge konnten nicht geladen werden.',
  deleteAllMatches: 'Alle Matches dieses Suchauftrags löschen',
  // Mandate status, and the actions that change it.
  status: {
    active: COMMON.active,
    paused: 'Pausiert',
    completed: 'Abgeschlossen',
  },
  /** Open-ended bounds of a search criterion. */
  from: (value: string) => `ab ${value}`,
  upTo: (value: string) => `bis ${value}`,
  activate: 'Aktivieren',
  pause: COMMON.pause,
  // A match's state, and the customer's reaction to it.
  matchStatus: {
    new: 'Neu',
    confirmed: 'Bestätigt',
    rejected: 'Verworfen',
  },
  customerFeedback: {
    liked: 'Gefällt Kunde',
    disliked: 'Gefällt Kunde nicht',
  },
  /** Heads the customer's own words about a match. */
  customerSaid: TERMS.customer,
  /** Opens the pictures at full size. */
  showPictures: 'Bilder ansehen',
  liked: 'Gefällt',
  disliked: 'Gefällt nicht',
  like: 'Gefällt mir',
  dislike: 'Gefällt mir nicht',
  changeRating: 'Meinung ändern',
  approve: COMMON.confirm,
  reject: COMMON.discard,
  sortBy: 'Sortieren nach',
  sortNewest: 'Neueste',
  sortFit: 'Passt am besten',
  budgetShare: (percent: number) => `${percent} % vom Budget`,
  /**
   * Why a value in a match row is marked. Exhaustive over `CriterionKey`, so
   * a criterion added to the matcher fails here rather than at the row that
   * looks its explanation up.
   */
  fit: {
    type: 'Anderer Objekttyp als gesucht',
    location: 'Nicht in der gesuchten Lage',
    price: 'Außerhalb des gesuchten Preises',
    size: 'Außerhalb der gesuchten Wohnfläche',
    rooms: 'Nicht die gesuchte Zimmerzahl',
    constructionYear: 'Außerhalb des gesuchten Baujahrs',
    leasehold: 'Erbbaurecht',
    groundFloor: 'Nicht im Erdgeschoss',
  } satisfies Record<CriterionKey, string>,
  viewOn: (source: string) => `Auf ${source} ansehen`,
  agentNote: AGENT_NOTE,
  // Agent notes modal
  agentNotesModalTitle: 'Match bestätigen',
  agentNotesModalText: `${AGENT_NOTE} (optional)`,
  agentNotesPlaceholder: `${AGENT_NOTE} …`,
  // Reject options
  rejectForCustomer: (customer: string) => `Verwerfen für ${customer}`,
  rejectAll: 'Verwerfen für alle Kunden',
  rejectAllModalTitle: 'Alle Matches zu diesem Objekt verwerfen',
  rejectAllModalMessage: (n: number) =>
    `Matches dieses Objekts für ${n} Kunden verwerfen?`,
  // Add match modal
  addMatch: 'Match hinzufügen',
  addMatchDescription:
    'Kunde wählen, für den dieses Objekt als Match angelegt wird.',
  selectCustomer: 'Kunde auswählen',
  searchCustomer: 'Kunde suchen …',
  matchCreated: 'Match wurde erfolgreich erstellt',
  matchAlreadyExists: 'Dieser Kunde hat für dieses Objekt bereits ein Match.',
  matchCreateError: 'Fehler beim Erstellen des Matches',
  // Delete all matches modal
  deleteAllMatchesModalMessage:
    'Alle Matches dieses Suchauftrags werden unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.',
  deleteAllMatchesConfirm: 'Alle löschen',
  matchesDeletedSuccess: (n: number) => `${n} Matches erfolgreich gelöscht`,
  matchesDeletedError: 'Fehler beim Löschen der Matches',
  // Status toggle
  statusUpdateError: 'Status konnte nicht geändert werden',
  // Sidebar filter panel
  searchMask: 'Suchmaske',
  listingUrlPlaceholder: 'https://…',
  constructionYearFilter: (year: number) =>
    `Baujahr bis ${year} (mind. 20 Jahre alt)`,

  errors: {
    mandateClosedInHubSpot:
      'Der Suchauftrag ist in HubSpot abgeschlossen (Won/Lost) und muss dort wieder geöffnet werden.',
    mandateStageWriteFailed:
      'Die Pipeline-Stage in HubSpot ließ sich nicht setzen — der Status bleibt unverändert.',
    mandateStageUnknown:
      'Die Pipeline-Stage konnte nicht aus HubSpot gelesen werden. Bitte später erneut versuchen.',
  },
} as const
